import React from "react";
import * as R from "ramda";
import { css } from "glamor";
import { Icon } from "antd";

import Table from "./Table";

const styles = {
    tr: css({
        borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    }),
    trGroup: css({
        borderBottom: "none !important",
    }),
    innerHeader: css({
        display: "none !important",
    }),
    titleContainer: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        whiteSpace: "normal",
    }),
    expanderIcon: css({
        marginRight: 5,
        width: 20,
    }),
};

const ExpanderIcon = ({ expanded, show, ...props }) => {
    const iconClassName = `${css(styles.expanderIcon, props.className)}`;
    return show ? (
        <Icon
            type={`${expanded ? "minus" : "plus"}-square`}
            className={iconClassName}
            {...props}
        />
    ) : (
        <div className={iconClassName} {...props} />
    );
};

const checkDepth = (data, depth = 0) =>
    data.map(item => ({
        ...item,
        children: item.children ? checkDepth(item.children, depth + 1) : [],
        depth,
    }));

// converts any array to {0: {}, 1: {}...} where key count is array length
const generateExpanded = data =>
    R.zipObj(
        data.map((item, i) => i),
        data.map(() => ({}))
    );

/**
 * Tree structure table for data with children
 * Renders collection of items and items inside "children" key with the same columns
 *
 * @param data - collection of items. [{...depth: 0, children: [{depth: 1}, ...]}, ...]
 * @param columns - columns. column with expander is not necessary
 * @param title - header of column with expander
 * @param expanderContent - what to render inside expander column next to expander icon
 * @param expanderId - id of expander column
 * @param getTrGroupProps
 * @param width - width of expander column
 * @param getTrProps
 * @param getTheadProps
 * @param expanded - "first" for default expanded first root element, "all" for default expanded all elements
 * @param rest
 * @returns {*}
 * @constructor
 */
const ChildrenTable = ({
    data,
    columns,
    title = "",
    expanderContent = () => null,
    expanderId = "expander",
    getTrGroupProps = () => ({}),
    width = 220,
    getTrProps = () => ({}),
    getTheadProps = () => ({}),
    expanded = false,
    ...rest
}) => {
    const tableColumns = [
        {
            id: expanderId,
            expander: true,
            Header: title,
            width,
            Expander: row => {
                return (
                    <div
                        className={`${styles.titleContainer}`}
                        data-at-id="table-row-title"
                        style={{
                            marginLeft: row.original.depth * 5,
                        }}
                    >
                        <ExpanderIcon
                            expanded={row.isExpanded}
                            show={
                                row.original.children &&
                                row.original.children.length
                            }
                        />
                        {expanderContent(row)}
                    </div>
                );
            },
        },
        ...columns,
    ];

    const generateTable = data => {
        const expandedScheme = {};
        const depth = data[0].depth;

        switch (expanded) {
            case "all":
                Object.assign(expandedScheme, generateExpanded(data));
                break;
            case "first":
                if (!depth) {
                    Object.assign(expandedScheme, { 0: {} });
                }
                break;
        }

        return (
            <Table
                getTheadProps={tableData => {
                    const superReturn = getTheadProps(tableData);
                    const isInner =
                        tableData.data.length && tableData.data[0].depth;
                    return {
                        className: `${css(
                            isInner && styles.innerHeader,
                            superReturn.className
                        )}`,
                        ...superReturn,
                    };
                }}
                getTrProps={tableData => {
                    const superReturn = getTrProps(tableData);
                    return {
                        ...superReturn,
                        className: `table-row-level-${depth} ${css(
                            styles.tr,
                            superReturn.className
                        )}`,
                        "data-at-id": `table-row-${depth}`,
                    };
                }}
                getTrGroupProps={tableData => {
                    const superReturn = getTrGroupProps(tableData);
                    return {
                        ...superReturn,
                        className: `${css(
                            styles.trGroup,
                            superReturn.className
                        )}`,
                    };
                }}
                data={data}
                columns={tableColumns}
                SubComponent={row => {
                    if (row.original.children.length) {
                        return generateTable(row.original.children);
                    }
                }}
                defaultExpanded={expandedScheme}
                defaultPageSize={data.length}
                showPagination={false}
                {...rest}
            />
        );
    };

    return generateTable(checkDepth(data));
};

export default ChildrenTable;
